import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import { CallMediaPermissionValue } from '@gem/call/CallStore/Call.types';
import {
  ICallPersonalizedPermissions,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallActionsPanelButton } from '@gem/call/components/CallActionsPanelButton/CallActionsPanelButton';
import { IcBackgroundEffectIcon, IcDoneIcon, IcVideoOff24Icon, IcVideoOn24Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IconView, PopoverMenu, IPopoverMenuItem } from '@gem/ui-kit';

import { useCallVideoButtonPresenter } from './CallVideoButton.presenter';
import styles from './CallVideoButton.styles.m.css';

interface ICallVideoButtonProps {
  checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean>;
  onVideoPopoverOpen?(): void;
  permissions: ICallPersonalizedPermissions | null;
  className?: string;
  videoPermission: CallMediaPermissionValue;
}

export const CallVideoButton = observer(
  forwardRef<() => void, ICallVideoButtonProps>((props, ref) => {
    const presenter = useCallVideoButtonPresenter({
      checkAndSendPermissionRequest: props.checkAndSendPermissionRequest,
      permissions: props.permissions,
      videoPermission: props.videoPermission,
      onVideoPopoverOpen: props.onVideoPopoverOpen,
    });

    const { t } = useTranslation();
    const audioButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
      ref,
      () => {
        return presenter.closeVideoPopover;
      },
      []
    );

    const videoMenuItems = useMemo(() => {
      const items: IPopoverMenuItem[] = [
        {
          key: 'title',
          title: t('calls_equipment_select_camera_header'),
          isDisabled: true,
          className: styles.menuTitle,
        },
      ];

      presenter.devices.forEach((item) => {
        items.push({
          key: item.deviceId,
          title: item.label,
          onClick: () => presenter.setCurrentDeviceId(item.deviceId),
          iconSuffix:
            item.deviceId === presenter.currentDeviceId ? (
              <div className={styles.checkIcon}>
                <IconView Icon={IcDoneIcon} />
              </div>
            ) : undefined,
        });
      });
      if (presenter.canUseBlur) {
        items.push({
          key: 'apply-delimeter',
          title: '',
          className: classNames(styles.menuTitle, styles.backgroundPanelDelimeter),
        });

        items.push({
          key: 'apply-background',
          icon: IcBackgroundEffectIcon,
          title: t('call_apply_background'),
          onClick: presenter.showBackgroundPanel,
        });
      }
      return items;
    }, [presenter.devices, presenter.currentDeviceId, presenter.setCurrentDeviceId]);

    return (
      <>
        <CallActionsPanelButton
          isActive={!presenter.isVideoMuted}
          isArrowButtonPressed={presenter.isVideoPopoverOpened}
          setRef={audioButtonRef}
          Icon={presenter.isVideoMuted ? IcVideoOff24Icon : IcVideoOn24Icon}
          onClick={presenter.isVideoMuted ? presenter.unmuteVideo : presenter.muteVideo}
          onArrowClick={
            presenter.isVideoPopoverOpened
              ? presenter.closeVideoPopover
              : presenter.onVideoArrowClick
          }
          className={props.className}
          theme={presenter.isVideoMuted ? '12' : '13'}
          isDisabled={
            !presenter.isCallStarted || props.videoPermission === CallMediaPermissionValue.Denied
          }
        />
        <PopoverMenu
          className={styles.popover}
          isOpened={presenter.isVideoPopoverOpened}
          placement="top-end"
          menuItems={videoMenuItems}
          reference={audioButtonRef.current}
          onClose={presenter.closeVideoPopover}
          theme={'dark'}
        />
      </>
    );
  })
);
